import { supabase } from '../lib/supabaseClient.ts';

export interface AlertVerification {
  id: string;
  title: string;
  source_type: string;
  created_at: string;
  alert_type: string;
  severity: string;
  isRealGovernmentData: boolean;
  dataSource: 'hardcoded' | 'rss_scraped' | 'manual_sample' | 'weather_generated';
}

export class AlertVerificationService {
  // Check all current alerts and classify their sources
  async verifyCurrentAlerts(): Promise<AlertVerification[]> {
    try {
      const { data: alerts, error } = await supabase
        .from('local_alerts')
        .select('id, title, source_type, created_at, alert_type, severity, description')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return alerts.map(alert => ({
        id: alert.id,
        title: alert.title,
        source_type: alert.source_type,
        created_at: alert.created_at,
        alert_type: alert.alert_type,
        severity: alert.severity,
        isRealGovernmentData: this.classifyAlertSource(alert),
        dataSource: this.getDataSource(alert)
      }));
    } catch (error) {
      console.error('Failed to verify alerts:', error);
      return [];
    }
  }

  // Classify if alert is from real government source
  private classifyAlertSource(alert: any): boolean {
    const realGovernmentSources = [
      'fews_net_api',
      'fews_net_verified',
      'reliefweb_api', 
      'reliefweb_verified',
      'world_bank_api',
      'world_bank_verified',
      'ministry_agriculture_ls',
      'lesotho_met_services',
      'fao_lesotho_office',
      'undp_lesotho',
      'weather_service' // Real weather data
    ];

    // Check if source indicates real government data
    const isRealSource = realGovernmentSources.some(source => 
      alert.source_type.toLowerCase().includes(source.toLowerCase())
    );

    return isRealSource;
  }

  // Determine the actual data source
  private getDataSource(alert: any): 'hardcoded' | 'rss_scraped' | 'manual_sample' | 'weather_generated' | 'real_api' {
    // Check for sample/hardcoded data patterns
    const samplePatterns = [
      'ministry_of_agriculture',
      'government_program',
      'veterinary_services',
      'market_support',
      'community_report',
      'agricultural_calendar'
    ];

    if (samplePatterns.includes(alert.source_type)) {
      return 'manual_sample';
    }

    // Check for real API data
    const realApiPatterns = [
      'fews_net_api',
      'fews_net_verified',
      'reliefweb_api',
      'reliefweb_verified', 
      'world_bank_api',
      'world_bank_verified',
      'ministry_agriculture_ls',
      'lesotho_met_services',
      'fao_lesotho_office',
      'undp_lesotho'
    ];

    if (realApiPatterns.some(pattern => alert.source_type.includes(pattern))) {
      return 'real_api';
    }

    // Check for weather generated
    if (alert.source_type === 'weather_service' && alert.alert_type === 'weather') {
      return 'weather_generated';
    }

    return 'hardcoded';
  }

  // Generate verification report
  async generateVerificationReport(): Promise<string> {
    const alerts = await this.verifyCurrentAlerts();
    
    const realCount = alerts.filter(a => a.isRealGovernmentData).length;
    const totalCount = alerts.length;
    const realPercentage = totalCount > 0 ? Math.round((realCount / totalCount) * 100) : 0;

    const sourceCounts = alerts.reduce((acc, alert) => {
      acc[alert.dataSource] = (acc[alert.dataSource] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    let report = `🔍 ALERT VERIFICATION REPORT\n`;
    report += `=====================================\n`;
    report += `Total Active Alerts: ${totalCount}\n`;
    report += `Real Government Data: ${realCount} (${realPercentage}%)\n`;
    report += `Sample/Mock Data: ${totalCount - realCount} (${100 - realPercentage}%)\n\n`;
    
    report += `📊 DATA SOURCE BREAKDOWN:\n`;
    Object.entries(sourceCounts).forEach(([source, count]) => {
      report += `• ${source.replace('_', ' ').toUpperCase()}: ${count} alerts\n`;
    });

    report += `\n📋 DETAILED ALERT LIST:\n`;
    alerts.forEach((alert, index) => {
      const status = alert.isRealGovernmentData ? '✅ REAL' : '❌ SAMPLE';
      report += `${index + 1}. ${status} - ${alert.title}\n`;
      report += `   Source: ${alert.source_type} | Type: ${alert.dataSource}\n`;
      report += `   Created: ${new Date(alert.created_at).toLocaleString()}\n\n`;
    });

    return report;
  }

  // Force fetch real government alerts now
  async forceRealAlertsFetch(): Promise<void> {
    try {
      console.log('🚀 FORCING REAL GOVERNMENT ALERTS FETCH...');
      
      // Try real government APIs first
      const { realGovernmentAlertsService } = await import('../services/realGovernmentAlerts.ts');
      await realGovernmentAlertsService.fetchRealAlerts();
      
      // Only create fallback if no real data exists
      await realGovernmentAlertsService.createFallbackAlertsIfNeeded();
      
      console.log('✅ Real government alerts fetch completed');
    } catch (error) {
      console.error('❌ Failed to force fetch real alerts:', error);
    }
  }
}

export const alertVerificationService = new AlertVerificationService();